import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToActionQueue } from "@/utils/mistralInsightMappers";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import {
  AlertCircle,
  ClipboardCheck,
  RefreshCw,
  ShieldCheck,
  User,
  XCircle,
} from "lucide-react";
import {
  buildApaarActionQueue,
  buildApaarExecutiveKpis,
  buildApaarExecutiveMarkdown,
  buildApaarFunnelNote,
  buildApaarFunnelRows,
  buildApaarGaugeStyle,
  buildApaarHeroHeadline,
  buildApaarHeroNarrative,
  buildApaarMonitoringTable,
  buildApaarPrimaryAction,
  buildApaarPriorityCards,
  buildApaarRatioDonuts,
  buildApaarStatusChips,
  buildApaarWorkstreams,
} from "@/utils/apaarDerived";
import "./ApaarExecutiveTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ACTION_ICONS = {
  orange: User,
  red: XCircle,
  "": RefreshCw,
};

const WORKSTREAM_ICONS = {
  orange: User,
  red: AlertCircle,
  "": RefreshCw,
  green: ClipboardCheck,
};

const ApaarExecutiveTab = ({ overview = {}, funnelData = [], riskData = {}, onNavigateTab }) => {
  const genRate = overview.generation_rate ?? 0;
  const hasData = Boolean(overview?.total_students);

  const buildLocalMarkdown = useCallback(
    () => buildApaarExecutiveMarkdown(overview, riskData),
    [overview, riskData]
  );

  const scopeKey = useMemo(
    () => JSON.stringify({ g: overview?.generation_rate, t: overview?.total_students }),
    [overview?.generation_rate, overview?.total_students]
  );

  const { loading: aiLoading, pickHeadline, pickPrimaryAction, pickFromBucket, isMistral, buckets } =
    useMistralNarrativeOverlay({
      kind: "apaar-executive",
      title: "APAAR Executive Summary",
      buildLocalMarkdown,
      context: { overview, riskData, funnelData },
      enabled: hasData,
      scopeKey,
    });

  const gaugeStyle = useMemo(() => buildApaarGaugeStyle(genRate), [genRate]);
  const localHeadline = useMemo(() => buildApaarHeroHeadline(overview), [overview]);
  const localNarrative = useMemo(() => buildApaarHeroNarrative(overview), [overview]);
  const headline = useMemo(
    () => (aiLoading ? localHeadline : pickHeadline(localHeadline)),
    [aiLoading, pickHeadline, localHeadline]
  );
  const narrative = useMemo(
    () => (aiLoading ? localNarrative : pickFromBucket(localNarrative, "insights", 1)),
    [aiLoading, pickFromBucket, localNarrative]
  );
  const statusChips = useMemo(() => {
    const chips = buildApaarStatusChips(overview);
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "purple" }, ...chips];
    }
    return chips;
  }, [overview, isMistral, aiLoading]);
  const localPrimaryAction = useMemo(
    () => buildApaarPrimaryAction(overview, riskData),
    [overview, riskData]
  );
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const kpis = useMemo(() => buildApaarExecutiveKpis(overview), [overview]);
  const priorityCards = useMemo(() => buildApaarPriorityCards(riskData), [riskData]);
  const funnelRows = useMemo(
    () => buildApaarFunnelRows(funnelData, overview.total_students),
    [funnelData, overview.total_students]
  );
  const funnelNote = useMemo(() => buildApaarFunnelNote(overview), [overview]);
  const ratioDonuts = useMemo(() => buildApaarRatioDonuts(overview), [overview]);
  const localActionQueue = useMemo(
    () => buildApaarActionQueue(overview, riskData),
    [overview, riskData]
  );
  const actionQueue = useMemo(() => {
    if (aiLoading) return localActionQueue;
    const aiActions = bulletsToActionQueue(
      parseInsightBullets(buckets.actions),
      localActionQueue
    );
    return aiActions?.length ? aiActions : localActionQueue;
  }, [aiLoading, buckets.actions, localActionQueue]);
  const monitoringRows = useMemo(
    () => buildApaarMonitoringTable(overview, riskData),
    [overview, riskData]
  );
  const workstreams = useMemo(() => buildApaarWorkstreams(overview), [overview]);

  return (
    <div className="apx-exec-tab" data-testid="apaar-executive">
      <section className="apx-hero-card" id="apx-exec-hero">
        <div className="apx-hero-layout">
          <div>
            <div className="apx-health-main">
              <div
                className="apx-gauge"
                style={gaugeStyle}
                aria-label={`APAAR generation rate ${genRate} percent`}
              >
                <div>
                  <strong>{genRate}%</strong>
                  <span>Generated</span>
                </div>
              </div>
              <div className="apx-health-copy">
                <h2>{toTitleCase(headline)}</h2>
                <p>{narrative}</p>
                <div className="apx-status-row">
                  {statusChips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`apx-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="apx-summary-note">
              <div className="apx-icon-box purple">
                <ShieldCheck size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="apx-metric-grid" aria-label="APAAR executive KPIs">
            {kpis.map((card) => (
              <article key={card.label} className={`apx-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="apx-priority-grid" aria-label="Priority alerts">
        {priorityCards.map((card) => (
          <article key={card.id} className={`apx-priority-card ${card.cardClass}`.trim()}>
            <small>{card.label}</small>
            <strong>{card.value}</strong>
            <p>{card.body}</p>
            <button
              type="button"
              className="apx-cta"
              onClick={() => onNavigateTab?.(card.tab)}
            >
              {card.cta}
            </button>
          </article>
        ))}
      </section>

      <div className="apx-section-title" id="apx-analytics">
        <div>
          <h2>Status Movement and Key Ratios</h2>
          <p>
            Charts are separated from KPI cards to avoid crowding. Users can first understand the
            health, then diagnose status distribution and ratios.
          </p>
        </div>
        <button
          type="button"
          className="apx-section-action"
          onClick={() => scrollToId("apx-actions")}
        >
          Go to action queue →
        </button>
      </div>

      <section className="apx-analytics-grid">
        <article className="apx-panel">
          <div className="apx-panel-head">
            <div>
              <h3>APAAR Status Funnel</h3>
              <p>
                Horizontal funnel makes generated, not-applied, failed and requested counts scannable
                without overloading the first fold.
              </p>
            </div>
            <span className="apx-pill">Student count</span>
          </div>
          <div className="apx-funnel-list" aria-label="APAAR status funnel chart">
            {funnelRows.map((row) => (
              <div key={row.status} className="apx-funnel-row">
                <div className="apx-funnel-label">{row.status}</div>
                <div className="apx-funnel-track">
                  <div
                    className={`apx-funnel-bar ${row.barClass}`.trim()}
                    style={{ width: `${row.widthPct}%` }}
                  />
                </div>
                <div className="apx-funnel-value">{row.count}</div>
              </div>
            ))}
          </div>
          <div className="apx-funnel-note">
            <b>Interpretation:</b> {funnelNote}
          </div>
        </article>

        <article className="apx-panel">
          <div className="apx-panel-head">
            <div>
              <h3>Key Metrics</h3>
              <p>Three ratios retained from the current tab, redesigned as consistent executive health rings.</p>
            </div>
            <span className="apx-pill">Ratio view</span>
          </div>
          <div className="apx-donut-grid">
            {ratioDonuts.map((donut) => (
              <div key={donut.label} className="apx-donut-card">
                <div
                  className="apx-mini-donut"
                  style={{
                    background: `radial-gradient(circle at center, white 0 58%, transparent 59%), conic-gradient(var(--apx-warning) 0 ${donut.pct}%, rgba(245,158,11,.15) ${donut.pct}% 100%)`,
                  }}
                >
                  <strong>{donut.display}</strong>
                </div>
                <b>{donut.label}</b>
                <span>{donut.sub}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="apx-section-title" id="apx-actions">
        <div>
          <h2>Action Queue and Operational Ownership</h2>
          <p>
            Executive Overview now ends with a clear action path: what to fix, who should own it,
            and what outcome to track in the next review.
          </p>
        </div>
        <button
          type="button"
          className="apx-section-action"
          onClick={() => scrollToId("apx-exec-hero")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="apx-two-column">
        <article className="apx-panel">
          <div className="apx-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Three high-impact actions for district and block officers.</p>
            </div>
            <span className="apx-pill">Priority</span>
          </div>
          <div className="apx-action-card">
            {actionQueue.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? RefreshCw;
              return (
                <div key={item.title} className="apx-action-item">
                  <div className={`apx-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                  <span className={`apx-action-badge ${item.badgeClass}`.trim()}>
                    {item.badge}
                  </span>
                </div>
              );
            })}
          </div>
        </article>

        <article className="apx-panel apx-table-card">
          <div className="apx-table-head">
            <div>
              <h3>Executive Monitoring Table</h3>
              <p>Compact summary for weekly review.</p>
            </div>
            <span className="apx-pill">This week</span>
          </div>
          <table className="apx-monitor-table" aria-label="APAAR executive monitoring table">
            <thead>
              <tr>
                <th>Focus Area</th>
                <th>Current Volume</th>
                <th>Risk</th>
                <th>Owner</th>
                <th>Review Action</th>
              </tr>
            </thead>
            <tbody>
              {monitoringRows.map((row) => (
                <tr key={row.focus}>
                  <td>{row.focus}</td>
                  <td className="score">{row.volume}</td>
                  <td>
                    <span className={`apx-tag ${row.riskClass}`.trim()}>{row.risk}</span>
                  </td>
                  <td>{row.owner}</td>
                  <td>{row.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>

      <div className="apx-section-title" id="apx-workstreams">
        <div>
          <h2>Workstream Cards</h2>
          <p>
            Operational work is split into simple cards so officers know exactly which workflow to
            start from this overview page.
          </p>
        </div>
        <button
          type="button"
          className="apx-section-action"
          onClick={() => scrollToId("apx-analytics")}
        >
          View diagnostics →
        </button>
      </div>

      <section className="apx-workstream-grid">
        {workstreams.map((card) => {
          const Icon = WORKSTREAM_ICONS[card.iconClass] ?? RefreshCw;
          return (
            <article key={card.title} className="apx-workstream-card">
              <div className={`apx-icon-box ${card.iconClass}`.trim()}>
                <Icon size={22} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.body}</p>
              <div className="apx-workstream-meta">
                <span>Owner: {card.owner}</span>
                <span>SLA: {card.sla}</span>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
};

export default ApaarExecutiveTab;
